import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/components/table";
import EmployeeInput from "components/employee/input";
import { Input } from "ui/components/input";
import { Progress } from "ui/components/progress";
import { ShimmerEffect } from "@/components/shimmer";
import Menu from "components/menu";
import { DropdownMenuItem } from "ui/components/dropdown-menu";
import classNames from "classnames";

export function PlanningTable({
  timesheet,
  weekData,
  getWeekTotal,
  getDayTotal,
  handleUserChange,
  getPlannableId,
  getPlannableTotal,
  handleHourChange,
  handleDeletePlannable,
  selectedUser,
  loading,
}) {
  // Helper function to check if a date is within the valid range
  const isDateInValidRange = (plannable, date) => {
    // If milestone exists, check milestone date range
    if (plannable.milestone?.start_date && plannable.milestone?.end_date) {
      const cellDate = new Date(date);
      const milestoneStart = new Date(plannable.milestone.start_date);
      const milestoneEnd = new Date(plannable.milestone.end_date);
      return cellDate >= milestoneStart && cellDate <= milestoneEnd;
    }

    // If no milestone but job exists, check job date range
    if (plannable.job?.start_date && plannable.job?.end_date) {
      const cellDate = new Date(date);
      const jobStart = new Date(plannable.job.start_date);
      const jobEnd = new Date(plannable.job.end_date);
      return cellDate >= jobStart && cellDate <= jobEnd;
    }

    // If no date ranges available, allow editing
    return true;
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="px-3.5 py-3 border-r-1">
              <EmployeeInput
                value={selectedUser}
                onChange={handleUserChange}
                set="plan"
                className="min-w-80"
              />
            </TableHead>
            {weekData.map((day) => (
              <TableHead key={day.fullDate} className="border-r-1">
                <span className="text-zinc-400">
                  {day.day}, {day.date} {day.month}
                </span>
                <div>{getDayTotal(day)}h</div>
                <Progress
                  value={Math.min((getDayTotal(day) / 8) * 100, 100)}
                  barClassName="bg-chart-2"
                />
              </TableHead>
            ))}
            <TableHead>
              {!loading ? (
                <>
                  <div>Total</div>
                  <div>{getWeekTotal()}h</div>
                </>
              ) : (
                <div className="space-y-2">
                  <ShimmerEffect className="h-4 w-12" />
                  <ShimmerEffect className="h-4 w-8" />
                </div>
              )}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {!loading
            ? timesheet.plannables.map((plannable) => (
                <TableRow key={getPlannableId(plannable)}>
                  <TableCell className="border-r-1 border-b-1 p-3.5 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {plannable.project.name}
                      </div>
                      <div className="text-muted-foreground">
                        <div>{plannable.job.name}</div>
                        {plannable.milestone?.name && (
                          <div className="mt-1 text-xs">
                            {plannable.milestone.name}
                          </div>
                        )}
                      </div>
                    </div>
                    <Menu>
                      <DropdownMenuItem
                        onClick={() => handleDeletePlannable(plannable)}
                      >
                        Delete
                      </DropdownMenuItem>
                    </Menu>
                  </TableCell>
                  {weekData.map((day) => {
                    const isExisting = plannable.existing_dates?.[day.fullDate];
                    const isInValidRange = isDateInValidRange(
                      plannable,
                      day.fullDate,
                    );
                    const isDisabled = isExisting || !isInValidRange;

                    return (
                      <TableCell
                        key={day.fullDate}
                        className={classNames(
                          "border-r-1 border-b-1",
                          isDisabled && "bg-neutral-50",
                        )}
                      >
                        <Input
                          type="number"
                          className="w-full text-center"
                          value={plannable?.planned_hours?.[day.fullDate] || ""}
                          onChange={(e) =>
                            handleHourChange(plannable, day, e.target.value)
                          }
                          disabled={isDisabled}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center border-b-1">
                    {getPlannableTotal(plannable)}h
                  </TableCell>
                </TableRow>
              ))
            : Array.from({ length: 2 }).map((_, index) => (
                <TableRow key={`ShimmerEffect-${index}`}>
                  <TableCell className="border-r-1 border-b-1 p-3.5 text-sm">
                    <div className="space-y-2">
                      <ShimmerEffect className="h-4 w-32" />
                      <ShimmerEffect className="h-3 w-24" />
                      <ShimmerEffect className="h-3 w-20" />
                    </div>
                  </TableCell>
                  {weekData.map((day) => (
                    <TableCell
                      key={`ShimmerEffect-${day.fullDate}`}
                      className="border-r-1 border-b-1"
                    >
                      <ShimmerEffect className="h-8 w-full" />
                    </TableCell>
                  ))}
                  <TableCell className="text-center">
                    <ShimmerEffect className="h-4 w-8 mx-auto" />
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </div>
  );
}
